import { useEffect, useMemo, useRef, useState } from "react";
import { DropFiles } from "./DropFiles";
import type { Pin } from "@centerline/core/viewer";
import { api, type SubmittalSource, type DocumentSummary, type SplitItem, type Submittal, type SubmittalList, type SubmittalPacket } from "./api";
import { canApprove, useMe } from "./session";
import { href, navigate } from "./router";
import { openPdf, type PDFDocumentProxy } from "./pdf";
import { Thumb } from "./Thumb";
import "./job.css";

export const SUBMITTAL_STATUSES = ["Pending", "Approved", "Approved as Noted", "Revise & Resubmit", "Rejected"];
const statusClass = (s: string) => `sub-status s-${s.toLowerCase().replace(/[^a-z]+/g, "-")}`;

/** Submittals tab: list, upload a packet, split it, place on plans. Office edits; foremen view. */
export function SubmittalsTab({ projectId, documents, tick }: { projectId: string; documents: DocumentSummary[]; tick: number }) {
  const office = canApprove(useMe());
  const [data, setData] = useState<SubmittalList | null>(null);
  const [archived, setArchived] = useState(false);
  const [splitting, setSplitting] = useState<SubmittalPacket | null>(null);
  const [placing, setPlacing] = useState<Submittal | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const load = () => api.submittals(projectId, archived).then(setData).catch((e) => setError(e.message));
  useEffect(() => { void load(); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, tick, archived]);
  const upload = async (f?: File) => {
    if (!f) return;
    setBusy(true); setError(null);
    try { const r = await api.uploadPacket(projectId, f); await load(); setSplitting(r.packet); }
    catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };
  const update = async (s: Submittal, body: Parameters<typeof api.updateSubmittal>[2]) => { setError(null); try { await api.updateSubmittal(projectId, s.id, body); await load(); } catch (e) { setError((e as Error).message); } };

  if (splitting) return <Splitter projectId={projectId} packet={splitting} onDone={() => { setSplitting(null); void load(); }} />;
  if (!data) return <div className="muted">{error ?? "Loading submittals…"}</div>;
  const unsplit = data.packets.filter((p) => !data.submittals.some((s) => s.packetId === p.id));
  return (
    <div className="submittals">
      {office && !archived && <DropFiles testId="drop-submittals" accept="application/pdf,.pdf" kind="a PDF" multiple={false} disabled={busy} label="Drop the submittal packet PDF here" onFiles={(fs) => void upload(fs[0])}><div className="jf-upload">
        <input ref={input} type="file" accept="application/pdf,.pdf" hidden onChange={(e) => { void upload(e.target.files?.[0]); e.target.value = ""; }} />
        <button className="btn primary" disabled={busy} onClick={() => input.current?.click()}>{busy ? "Uploading…" : "+ Upload submittal packet (PDF)"}</button>
        <span className="muted small">Then mark where each submittal starts.<span className="drop-hint"> Or drag the PDF here.</span></span>
      </div></DropFiles>}
      {error && <div className="error">{error}</div>}
      {office && unsplit.length > 0 && <div className="card sub-packets">
        <b>Packets not split yet</b>
        {unsplit.map((p) => <div key={p.id} className="row"><span className="grow">{p.name} <span className="muted small">· {p.pages} pages</span></span><button className="btn small primary" onClick={() => setSplitting(p)}>Split</button></div>)}
      </div>}
      {!data.submittals.length && <div className="empty">{archived ? "Nothing archived." : `No submittals yet.${office ? "" : " The office adds these."}`}</div>}
      <div className="jf-list">
        {data.submittals.map((s) => (
          <div key={s.id} className="card sub-row" data-submittal-id={s.id}>
            <a className="jf-open" href={api.submittalUrl(projectId, s.id)} target="_blank" rel="noreferrer">
              <span className="sub-no">{s.number}</span>
              <span className="grow"><span className="title">{s.title}</span>
                <span className="muted small">{s.spec ? `Spec ${s.spec} · ` : ""}{s.pages} page{s.pages === 1 ? "" : "s"}{s.pins.length ? " · 📍 on plan" : ""}</span></span>
            </a>
            <div className="jf-actions">
              {office ? <select className={`small ${statusClass(s.status)}`} aria-label="Status" value={s.status} onChange={(e) => void update(s, { status: e.target.value })}>{SUBMITTAL_STATUSES.map((x) => <option key={x}>{x}</option>)}</select>
                : <span className={statusClass(s.status)}>{s.status}</span>}
              {s.pins[0] && <a className="btn small" href={href({ name: "viewer", id: s.pins[0].documentId, page: 1, pin: s.pins[0].pinId })}>Show on plan</a>}
              {office && !archived && <button className="btn small" onClick={() => setPlacing(s)}>Place on plan</button>}
              {office && <button className="btn small" onClick={() => { if (archived || confirm(`Archive ${s.number}? It's hidden but kept.`)) void update(s, { archived: !archived }); }}>{archived ? "Restore" : "Archive"}</button>}
            </div>
          </div>
        ))}
      </div>
      {(data.archivedCount > 0 || archived) && office && <button className="btn small" onClick={() => setArchived(!archived)}>{archived ? "← Back" : `Archived (${data.archivedCount})`}</button>}
      {placing && <PlacePicker submittal={placing} documents={documents.filter((d) => !d.archivedAt)} onClose={() => setPlacing(null)} />}
    </div>
  );
}

function PlacePicker({ submittal, documents, onClose }: { submittal: Submittal; documents: DocumentSummary[]; onClose: () => void }) {
  const [doc, setDoc] = useState(documents.find((d) => d.working)?.id ?? documents[0]?.id ?? "");
  const [page, setPage] = useState(1);
  const d = documents.find((x) => x.id === doc);
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal card" role="dialog" aria-label="Place on plan" onClick={(e) => e.stopPropagation()}>
        <h3>Place {submittal.number} on a plan</h3>
        {!documents.length ? <p className="muted">Upload a plan set first.</p> : <>
          <label>Plan set<select value={doc} onChange={(e) => { setDoc(e.target.value); setPage(1); }}>{documents.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}</select></label>
          <label>Sheet<select aria-label="Sheet" value={page} onChange={(e) => setPage(Number(e.target.value))}>{Array.from({ length: d?.pageCount ?? 1 }, (_, i) => <option key={i} value={i + 1}>Sheet {i + 1}</option>)}</select></label>
          <p className="muted small">Next, tap the spot on the sheet. You can flip sheets there too.</p>
        </>}
        <div className="row"><button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn primary" disabled={!d} onClick={() => navigate({ name: "viewer", id: doc, page, place: submittal.id })}>Go to sheet</button></div>
      </div>
    </div>
  );
}

const SUB_NO = /submittal\s*(?:no\.?|number|#)\s*[:#.]?\s*([A-Z0-9][A-Z0-9.\-]*)/i;
const SPEC = /(?:spec(?:ification)?\s*(?:section)?|section)\s*[:#]?\s*(\d{2}\s?\d{2}\s?\d{2}(?:\.\d+)?)/i;
function guessTitle(text: string): string {
  const lines = text.split(/\n/).map((l) => l.replace(/\s+/g, " ").trim()).filter((l) => l.length > 3 && !SUB_NO.test(l) && !/^(date|page|project|to|from)\b/i.test(l) && /[a-z]/i.test(l));
  const t = lines.find((l) => /^(title|description|item|subject)\s*[:\-]/i.test(l))?.replace(/^\w+\s*[:\-]\s*/, "") ?? lines[0] ?? "";
  return t.slice(0, 90);
}

/** Mark the first page of each submittal; each range becomes its own PDF. */
function Splitter({ projectId, packet, onDone }: { projectId: string; packet: SubmittalPacket; onDone: () => void }) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [texts, setTexts] = useState<string[]>([]);
  const [starts, setStarts] = useState<Set<number>>(() => new Set([1]));
  const [meta, setMeta] = useState<Record<number, Partial<SplitItem>>>({});
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    const task = openPdf(api.packetUrl(projectId, packet.id));
    task.promise.then(async (p) => {
      if (!alive) return; setPdf(p);
      const out: string[] = [];
      for (let i = 1; i <= Math.min(p.numPages, 400); i++) {
        try { const c = await (await p.getPage(i)).getTextContent(); out.push(c.items.map((it) => ("str" in it ? it.str + (it.hasEOL ? "\n" : " ") : "")).join("")); } catch { out.push(""); }
      }
      if (alive) setTexts(out);
    }).catch((e) => setNote(`Couldn't open the packet: ${(e as Error).message}`));
    return () => { alive = false; void task.destroy(); };
  }, [projectId, packet.id]);
  const ranges = useMemo(() => {
    const s = [...starts].sort((a, b) => a - b);
    return s.map((from, i) => ({ from, to: (s[i + 1] ?? packet.pages + 1) - 1 }));
  }, [starts, packet.pages]);
  const toggle = (p: number) => { if (p === 1) return; const n = new Set(starts); if (n.has(p)) n.delete(p); else n.add(p); setStarts(n); };
  const suggest = () => {
    const found = new Set([1]); const m: Record<number, Partial<SplitItem>> = {};
    texts.forEach((t, i) => { const no = SUB_NO.exec(t); if (no) { found.add(i + 1); m[i + 1] = { number: no[1].replace(/[.\-]+$/, "") }; } });
    if (found.size === 1 && !m[1]) { setNote("No 'Submittal No.' cover sheets found. Tap the pages where each submittal starts."); return; }
    setStarts(found); setMeta((old) => { const n = { ...old }; for (const [k, v] of Object.entries(m)) n[Number(k)] = { ...n[Number(k)], ...v }; return n; });
    setNote(`Found ${found.size} submittal${found.size === 1 ? "" : "s"}. Check the ranges below.`);
  };
  const field = (from: number, k: keyof SplitItem, i: number): string => {
    const v = meta[from]?.[k];
    if (v !== undefined) return String(v);
    const t = texts[from - 1] ?? "";
    if (k === "number") return String(i + 1).padStart(3, "0");
    if (k === "title") return guessTitle(t);
    if (k === "spec") return SPEC.exec(t)?.[1] ?? "";
    if (k === "status") return "Pending";
    return "";
  };
  const set = (from: number, k: keyof SplitItem, v: string) => setMeta((m) => ({ ...m, [from]: { ...m[from], [k]: v } }));
  const save = async () => {
    setBusy(true); setNote(null);
    try {
      await api.splitPacket(projectId, packet.id, ranges.map((r, i) => ({ from: r.from, to: r.to, number: field(r.from, "number", i), title: field(r.from, "title", i) || `Pages ${r.from}–${r.to}`, spec: field(r.from, "spec", i), status: field(r.from, "status", i) })));
      onDone();
    } catch (e) { setNote((e as Error).message); setBusy(false); }
  };
  return (
    <div className="splitter">
      <div className="row"><button className="btn small" onClick={onDone}>← Back</button><b className="grow">Split {packet.name}</b></div>
      <p className="muted small">Tap the first page of each submittal (page 1 always starts one). {pdf ? `${packet.pages} pages.` : "Loading pages…"}</p>
      <div className="row"><button className="btn small" disabled={!texts.length} onClick={suggest}>✨ Auto-suggest split</button>{note && <span className="small">{note}</span>}</div>
      <div className="split-grid">
        {pdf && Array.from({ length: pdf.numPages }, (_, i) => i + 1).map((p) => (
          <button key={p} type="button" className={`split-page${starts.has(p) ? " start" : ""}`} data-page={p} aria-pressed={starts.has(p)} aria-label={`Page ${p}${starts.has(p) ? ", starts a submittal" : ""}`} onClick={() => toggle(p)}>
            <Thumb pdf={pdf} index={p - 1} />
            <span className="split-label">{starts.has(p) ? `▶ #${ranges.findIndex((r) => r.from === p) + 1} · ` : ""}p{p}</span>
          </button>
        ))}
      </div>
      <h4>{ranges.length} submittal{ranges.length === 1 ? "" : "s"}</h4>
      <div className="split-ranges">
        {ranges.map((r, i) => (
          <div key={r.from} className="card split-range" data-range={i + 1}>
            <div className="muted small">Pages {r.from}{r.to > r.from ? `–${r.to}` : ""}</div>
            <div className="split-fields">
              <label>No.<input aria-label={`Number ${i + 1}`} value={field(r.from, "number", i)} onChange={(e) => set(r.from, "number", e.target.value)} /></label>
              <label className="grow">Title<input aria-label={`Title ${i + 1}`} value={field(r.from, "title", i)} onChange={(e) => set(r.from, "title", e.target.value)} /></label>
              <label>Spec section<input aria-label={`Spec ${i + 1}`} placeholder="optional" value={field(r.from, "spec", i)} onChange={(e) => set(r.from, "spec", e.target.value)} /></label>
              <label>Status<select aria-label={`Status ${i + 1}`} value={field(r.from, "status", i)} onChange={(e) => set(r.from, "status", e.target.value)}>{SUBMITTAL_STATUSES.map((x) => <option key={x}>{x}</option>)}</select></label>
            </div>
          </div>
        ))}
      </div>
      <button className="btn primary" disabled={busy || !pdf} onClick={save}>{busy ? "Making PDFs…" : `Create ${ranges.length} submittal${ranges.length === 1 ? "" : "s"}`}</button>
    </div>
  );
}

/** Shown in the plan pin panel for a Submittal pin placed from the Submittals tab. */
export function SubmittalPinCard({ projectId, submittalId }: { projectId: string; submittalId: string }) {
  const [s, setS] = useState<Submittal | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => { api.submittal(projectId, submittalId).then((r) => setS(r.submittal)).catch((e) => setErr(e.message)); }, [projectId, submittalId]);
  if (err) return <div className="error small">{err}</div>;
  if (!s) return <div className="muted small">Loading submittal…</div>;
  return (
    <div className="card sub-pin-card" data-testid="submittal-card">
      <div><b>Submittal {s.number}</b> <span className={statusClass(s.status)}>{s.status}</span></div>
      <div className="title">{s.title}</div>
      <div className="muted small">{s.spec ? `Spec ${s.spec} · ` : ""}{s.pages} page{s.pages === 1 ? "" : "s"}{s.archivedAt ? " · archived" : ""}{s.source ? ` · from ${s.source.name} p${s.source.pages.join(",")}` : ""}</div>
      <InlinePages url={api.submittalUrl(projectId, s.id)} />
      <div className="row">
        <a className="btn primary" href={api.submittalUrl(projectId, s.id)} target="_blank" rel="noreferrer">📄 Open submittal PDF</a>
        <a className="btn small" href={href({ name: "project", id: projectId, tab: "submittals" })}>All submittals</a>
      </div>
    </div>
  );
}

/** The submittal's pages drawn inline (first 3) so the structure's submittal reads without opening the PDF. */
function InlinePages({ url }: { url: string }) {
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  useEffect(() => { const t = openPdf(url); t.promise.then(setPdf).catch(() => undefined); return () => { void t.destroy(); }; }, [url]);
  const w = Math.min(320, (typeof window !== "undefined" ? window.innerWidth : 400) - 70);
  if (!pdf) return <div className="muted small">Loading page…</div>;
  return <div className="sub-inline" data-testid="submittal-page">{Array.from({ length: Math.min(pdf.numPages, 3) }, (_, i) => <Thumb key={i} pdf={pdf} index={i} width={w} />)}{pdf.numPages > 3 && <div className="muted small">+{pdf.numPages - 3} more pages in the PDF</div>}</div>;
}

const KIND_LABEL: Record<string, string> = { file: "Files › Submittals", packet: "Uploaded packet", submittal: "Existing submittal" };

/** "Pin submittal" on a sheet: after tapping the structure, pick a packet/PDF, choose pages, attach. */
export function AttachSubmittal({ projectId, spot, onCancel, onDone }: { projectId: string; spot: { documentId: string; pageId: string; x: number; y: number }; onCancel: () => void; onDone: (pin: Pin) => void }) {
  const [sources, setSources] = useState<SubmittalSource[] | null>(null);
  const [src, setSrc] = useState<SubmittalSource | null>(null);
  const [pdf, setPdf] = useState<PDFDocumentProxy | null>(null);
  const [texts, setTexts] = useState<string[]>([]);
  const [picked, setPicked] = useState<Set<number>>(new Set());
  const [title, setTitle] = useState(""); const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false); const [err, setErr] = useState<string | null>(null);
  const opened = useRef(Date.now()); // the tap that opened us also fires a click on the backdrop
  const dismiss = () => { if (Date.now() - opened.current > 500) onCancel(); };
  useEffect(() => { api.submittalSources(projectId).then((r) => setSources(r.sources)).catch((e) => setErr(e.message)); }, [projectId]);
  useEffect(() => {
    if (!src) return;
    let alive = true; setPdf(null); setTexts([]); setPicked(new Set()); setTouched(false);
    const t = openPdf(src.url);
    t.promise.then(async (p) => {
      if (!alive) return; setPdf(p);
      const out: string[] = [];
      for (let i = 1; i <= Math.min(p.numPages, 300); i++) { try { const c = await (await p.getPage(i)).getTextContent(); out.push(c.items.map((it) => ("str" in it ? it.str + (it.hasEOL ? "\n" : " ") : "")).join("")); } catch { out.push(""); } }
      if (alive) setTexts(out);
    }).catch((e) => setErr(`Couldn't open that PDF: ${(e as Error).message}`));
    return () => { alive = false; void t.destroy(); };
  }, [src]);
  const first = [...picked].sort((a, b) => a - b)[0];
  const firstText = first ? texts[first - 1] ?? "" : "";
  useEffect(() => { if (!touched) setTitle(first ? guessTitle(firstText) || `${src?.name ?? ""} p${first}` : ""); }, [first, firstText, touched, src]);
  const toggle = (p: number) => { const n = new Set(picked); if (n.has(p)) n.delete(p); else n.add(p); setPicked(n); };
  const attach = async () => {
    if (!src || !picked.size) return;
    setBusy(true); setErr(null);
    try {
      const no = SUB_NO.exec(firstText)?.[1]?.replace(/[.\-]+$/, "");
      const r = await api.attachSubmittal(projectId, { kind: src.kind, sourceId: src.id, pages: [...picked].sort((a, b) => a - b), title, number: no, spec: SPEC.exec(firstText)?.[1] ?? "", status: "Pending", ...spot });
      onDone(r.pin);
    } catch (e) { setErr((e as Error).message); setBusy(false); }
  };
  const w = Math.min(340, (typeof window !== "undefined" ? window.innerWidth : 400) - 56);
  return (
    <div className="modal-backdrop" onClick={dismiss} onPointerDown={(e) => e.stopPropagation()}>
      <div className="modal card attach-sub" role="dialog" aria-label="Pin submittal" onClick={(e) => e.stopPropagation()}>
        <div className="row"><b className="grow">{src ? src.name : "Pick a submittal packet or PDF"}</b><button className="btn small" onClick={src ? () => setSrc(null) : onCancel}>{src ? "← Back" : "Cancel"}</button></div>
        {err && <div className="error small">{err}</div>}
        {!src && (!sources ? <div className="muted">Loading…</div> : !sources.length ? <div className="empty">No submittal PDFs yet. Add them in Files › Submittals or the Submittals tab.</div> :
          <div className="attach-sources">{sources.map((x) => (
            <button key={`${x.kind}-${x.id}`} className="attach-source" data-kind={x.kind} onClick={() => setSrc(x)}>
              <b>{x.name}</b><span className="muted small">{KIND_LABEL[x.kind]}{x.pages ? ` · ${x.pages} page${x.pages === 1 ? "" : "s"}` : ""}</span>
            </button>))}</div>)}
        {src && <>
          <p className="muted small">Tap the page(s) for this structure. {pdf ? `${pdf.numPages} pages.` : "Loading pages…"}</p>
          <div className="attach-pages">
            {pdf && Array.from({ length: pdf.numPages }, (_, i) => i + 1).map((p) => (
              <button key={p} type="button" className={`attach-page${picked.has(p) ? " on" : ""}`} data-page={p} aria-pressed={picked.has(p)} aria-label={`Page ${p}`} onClick={() => toggle(p)}>
                <Thumb pdf={pdf} index={p - 1} width={w} /><span className="split-label">{picked.has(p) ? "✓ " : ""}Page {p}</span>
              </button>))}
          </div>
          <div className="attach-foot">
            <label>Title<input aria-label="Submittal title" value={title} onChange={(e) => { setTouched(true); setTitle(e.target.value); }} placeholder="Pick a page first" /></label>
            <button className="btn primary" disabled={busy || !picked.size} onClick={attach}>{busy ? "Attaching…" : picked.size ? `Attach ${picked.size} page${picked.size === 1 ? "" : "s"}` : "Pick pages"}</button>
          </div>
        </>}
      </div>
    </div>
  );
}
